<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { Check } from 'lucide-vue-next';
import { authAPI } from '../services/api';
import { useAuthState } from '../composables/useAuthState';

const router = useRouter();
const { t } = useI18n();
const { getPersistedData, clearPersistedData } = useAuthState();

const firstName = ref('');
const lastName = ref('');
const company = ref('');
const setupToken = ref<string | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);

const isStepValid = computed(() => {
  return firstName.value && lastName.value && company.value;
});

const getButtonText = () => {
  if (isLoading.value) return t('complete_setup.loading');
  return t('complete_setup.submit');
};

onMounted(() => {
  // Get setup token from localStorage (set after OAuth login)
  setupToken.value = localStorage.getItem('setup_token');

  // Try to load persisted data from registration
  const persistedData = getPersistedData();
  if (persistedData) {
    firstName.value = persistedData.firstName || '';
    lastName.value = persistedData.lastName || '';
    company.value = persistedData.company || '';
  }

  // If no setup token, redirect to messenger or login
  if (!setupToken.value) {
    const token = localStorage.getItem('token');
    if (token) {
      router.push('/messenger');
    } else {
      router.push('/login');
    }
  }
});

const handleCompleteSetup = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await authAPI.completeSetup({
      setup_token: setupToken.value || '',
      first_name: firstName.value,
      last_name: lastName.value,
      organization_name: company.value,
    });

    // Clear persisted data
    clearPersistedData();
    localStorage.removeItem('setup_token');

    // Save token if provided
    if (response.token) {
      localStorage.setItem('token', response.token);
    }

    router.push('/messenger');
  } catch (err: any) {
    error.value = err.response?.data?.message || t('complete_setup.error_generic');
    console.error('Complete setup error:', err);
  } finally {
    isLoading.value = false;
  }
};

const handleSubmit = (e: Event) => {
  e.preventDefault();
  handleCompleteSetup();
};
</script>

<template>
  <div class="min-h-screen bg-[#18181C] flex items-center justify-center p-6">
    <div class="w-full max-w-md">
      <div class="bg-[#111113] rounded-3xl shadow-sm border border-[#2A2018] p-8 md:p-10">
        <!-- Header -->
        <div class="mb-8 text-center">
          <div
            class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Check class="w-8 h-8 text-blue-600" />
          </div>
          <h1 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('complete_setup.title') }}</h1>
          <p class="text-[#9E9080]">{{ $t('complete_setup.subtitle') }}</p>
        </div>

        <!-- Form -->
        <form @submit="handleSubmit">
          <div class="space-y-5">
            <div>
              <label for="firstName" class="block text-sm text-[#F5F0EA] mb-2">
                {{ $t('complete_setup.first_name_label') }}
              </label>
              <input
                type="text"
                id="firstName"
                required
                v-model="firstName"
                class="w-full px-4 py-3 bg-[#18181C] border border-[#2A2018] rounded-2xl focus:bg-[#111113] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition text-[#F5F0EA] placeholder:text-[#524840]"
                :placeholder="$t('complete_setup.first_name_placeholder')"
              />
            </div>
            <div>
              <label for="lastName" class="block text-sm text-[#F5F0EA] mb-2">
                {{ $t('complete_setup.last_name_label') }}
              </label>
              <input
                type="text"
                id="lastName"
                required
                v-model="lastName"
                class="w-full px-4 py-3 bg-[#18181C] border border-[#2A2018] rounded-2xl focus:bg-[#111113] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition text-[#F5F0EA] placeholder:text-[#524840]"
                :placeholder="$t('complete_setup.last_name_placeholder')"
              />
            </div>
            <div>
              <label for="company" class="block text-sm text-[#F5F0EA] mb-2">
                {{ $t('complete_setup.company_label') }}
              </label>
              <input
                type="text"
                id="company"
                required
                v-model="company"
                class="w-full px-4 py-3 bg-[#18181C] border border-[#2A2018] rounded-2xl focus:bg-[#111113] focus:ring-2 focus:ring-[#D4956A] focus:border-transparent outline-none transition text-[#F5F0EA] placeholder:text-[#524840]"
                :placeholder="$t('complete_setup.company_placeholder')"
              />
            </div>

            <div
              v-if="error"
              class="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"
            >
              {{ error }}
            </div>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="!isStepValid || isLoading"
            class="w-full flex items-center justify-center gap-2 px-5 py-3 mt-8 bg-[#0A0A0B] text-[#F5F0EA] rounded-2xl hover:bg-[#111113] disabled:bg-[#1E1E23] disabled:text-[#524840] disabled:cursor-not-allowed transition"
          >
            <span
              v-if="isLoading"
              class="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
            ></span>
            {{ getButtonText() }}
          </button>
        </form>

        <!-- Back to Login Link -->
        <p class="mt-8 text-center text-sm text-[#9E9080]">
          {{ $t('complete_setup.already_have_account') }}
          <router-link to="/login" class="text-[#F5F0EA] hover:underline">
            {{ $t('complete_setup.sign_in') }}
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>
